import { useState, useEffect, useCallback, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { api } from '../api/client';
import { useT } from '../i18n';

function fmtH(h: number): string {
  return h % 1 === 0 ? `${h} h` : `${h.toFixed(1).replace('.', ',')} h`;
}

type MetricLevel = 'ok' | 'warn' | 'err';
function daysLevel(d: number | null): MetricLevel { return d === null ? 'ok' : d <= 15 ? 'err' : d <= 30 ? 'warn' : 'ok'; }
// Hours shown neutrally — no compliance threshold

function presenceLevel(r: number | null): MetricLevel { return r === null ? 'ok' : r >= 80 ? 'ok' : r >= 60 ? 'warn' : 'err'; }
function journalLevel(d: number | null): MetricLevel { return d === null ? 'ok' : d <= 7 ? 'ok' : d <= 14 ? 'warn' : 'err'; }
function levelColor(l: MetricLevel): string { return l === 'ok' ? 'var(--ok)' : l === 'warn' ? 'var(--warn)' : 'var(--err)'; }

type SortKey =
  | 'name' | 'programme' | 'measures' | 'months' | 'days' | 'hours'
  | 'taux_activite' | 'taux_cible' | 'presence' | 'journal'
  | 'mandant' | 'rm' | 'rp';
type SortDir = 'asc' | 'desc';

function getVal(p: any, key: SortKey): number | string {
  switch (key) {
    case 'name': return `${p.last_name} ${p.first_name}`.toLowerCase();
    case 'programme': return (p.subtype_name || p.programme_code || '').toLowerCase();
    case 'measures': return p.measure_count ?? 0;
    case 'months': return p.total_months ?? 0;
    case 'days': return p.days_remaining ?? 9999;
    case 'hours': return p.hours_per_week ?? -1;
    case 'taux_activite': return p.taux_activite ?? -1;
    case 'taux_cible': return p.taux_cible ?? -1;
    case 'presence': return p.presence_rate_30d ?? -1;
    case 'journal': return p.journal_days_stale ?? 9999;
    case 'mandant': return (p.mandant_name || '').toLowerCase();
    case 'rm': return `${p.rm_last_name || ''} ${p.rm_first_name || ''}`.trim().toLowerCase();
    case 'rp': return `${p.rp_last_name || ''} ${p.rp_first_name || ''}`.trim().toLowerCase();
    default: return 0;
  }
}

// Render a string that's truncated past `max` chars and, on hover, overlays the full text
// with a background so it stays legible even when it overlaps the next cell.
function TruncText({ value, max }: { value: string; max: number }) {
  const v = value ?? '';
  if (v.length <= max) return <>{v}</>;
  return (
    <span className="cell-trunc" data-full={v}>
      {v.slice(0, max)}...
    </span>
  );
}

// Returns the first family name from a (possibly compound, space-separated) last_name.
// "Dupont Martin" → "Dupont". Hyphenated names like "Bouquet-Dupont" stay intact.
// Exported for the sidebar participant tabs, which label themselves the same way.
export function firstFamilyName(lastName: string | null | undefined): string {
  if (!lastName) return '';
  const trimmed = lastName.trim();
  if (!trimmed) return '';
  return trimmed.split(/\s+/)[0];
}

// Classify the participant's *current* mesure track by its mandant org name.
function currentTrack(mandantName: string | null | undefined): 'oai' | 'ri' | null {
  if (!mandantName) return null;
  const n = mandantName.toLowerCase();
  if (n.includes('oai')) return 'oai';
  if (/csr|csir|arasol|araspe|unité commune|unité insertion|association régionale/.test(n)) return 'ri';
  return null;
}

// Values shown in the columns, reused for both the dropdown filters and the Excel export
// so what you filter on is exactly what you see.
function typeDeMesure(p: any): string { return p.subtype_name || p.programme_code || ''; }
function fullName(last: string | null | undefined, first: string | null | undefined): string {
  return `${last || ''} ${first || ''}`.trim();
}

// Distinct non-empty values of `pick` across rows, sorted for the dropdown.
function distinctValues(rows: any[], pick: (p: any) => string): string[] {
  const set = new Set<string>();
  for (const p of rows) {
    const v = pick(p);
    if (v) set.add(v);
  }
  return [...set].sort((a, b) => a.localeCompare(b));
}

interface FilterSelectProps {
  value: string;
  onChange: (v: string) => void;
  options: string[];
  allLabel: string;
}

function FilterSelect({ value, onChange, options, allLabel }: FilterSelectProps) {
  return (
    <select
      className="form-input"
      value={value}
      onChange={e => onChange(e.target.value)}
      style={{
        maxWidth: 200,
        padding: '6px 10px',
        fontSize: 'var(--t-12)',
        color: value ? 'var(--ink-1)' : 'var(--ink-3)',
      }}
    >
      <option value="">{allLabel}</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

interface SortHeaderProps {
  sortKey: SortKey;
  label: string;
  currentKey: SortKey;
  currentDir: SortDir;
  onSort: (k: SortKey) => void;
  align?: 'left' | 'center';
  title?: string;
}

function SortHeader({ sortKey, label, currentKey, currentDir, onSort, align = 'left', title }: SortHeaderProps) {
  const active = sortKey === currentKey;
  return (
    <th
      onClick={() => onSort(sortKey)}
      title={title}
      style={{
        cursor: 'pointer',
        userSelect: 'none',
        textAlign: align,
        verticalAlign: 'bottom',
        lineHeight: 1.2,
        color: active ? 'var(--ink-1)' : undefined,
      }}
    >
      {label}
      <span style={{ marginLeft: 4, color: active ? 'var(--accent)' : 'var(--ink-3)', fontSize: 10, whiteSpace: 'nowrap' }}>
        {active ? (currentDir === 'asc' ? '▲' : '▼') : '↕'}
      </span>
    </th>
  );
}

interface PortfolioPageProps {
  /** False while a participant tab covers the portfolio (kept mounted so its
   * filters/sort survive); flipping back to true re-fetches the data. */
  active?: boolean;
  /** Opens the participant in an indented tab below Portfolio, instead of a
   * plain hash navigation that would unmount (and reset) this page. */
  onOpenParticipant: (id: number, measure: number | undefined, familyName: string) => void;
}

export function PortfolioPage({ active = true, onOpenParticipant }: PortfolioPageProps) {
  const { t } = useT();
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [filter, setFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [mandantFilter, setMandantFilter] = useState('');
  const [rmFilter, setRmFilter] = useState('');
  const [rpFilter, setRpFilter] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.getPortfolio({ tab: 'active' }) as any;
      setData(result.participants || []);
    } catch (err) {
      console.error('Failed to load portfolio:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on first activation, then again every time the portfolio becomes the
  // visible page again (a drilldown may have changed rates/plans underneath).
  useEffect(() => { if (active) fetchData(); }, [active, fetchData]);

  const onSort = (k: SortKey) => {
    if (k === sortKey) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(k);
      // Numeric metrics default to desc (worst-first feel); textual to asc.
      const numeric: SortKey[] = ['measures', 'months', 'hours', 'taux_activite', 'taux_cible'];
      const ascDefault: SortKey[] = ['days', 'journal', 'name', 'programme', 'mandant', 'rm', 'rp'];
      setSortDir(numeric.includes(k) ? 'desc' : ascDefault.includes(k) ? 'asc' : 'desc');
    }
  };

  // Dropdown options come from the loaded data, so they only ever list values that exist.
  const typeOptions = useMemo(() => distinctValues(data, typeDeMesure), [data]);
  const mandantOptions = useMemo(() => distinctValues(data, p => p.mandant_name || ''), [data]);
  const rmOptions = useMemo(() => distinctValues(data, p => fullName(p.rm_last_name, p.rm_first_name)), [data]);
  const rpOptions = useMemo(() => distinctValues(data, p => fullName(p.rp_last_name, p.rp_first_name)), [data]);

  // Filter + sort
  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const filtered = data.filter(p => {
      if (typeFilter && typeDeMesure(p) !== typeFilter) return false;
      if (mandantFilter && (p.mandant_name || '') !== mandantFilter) return false;
      if (rmFilter && fullName(p.rm_last_name, p.rm_first_name) !== rmFilter) return false;
      if (rpFilter && fullName(p.rp_last_name, p.rp_first_name) !== rpFilter) return false;
      if (!q) return true;
      const haystack = [
        p.first_name, p.last_name,
        p.subtype_name, p.programme_code, p.programme_label,
        p.mandant_name,
        p.rm_first_name, p.rm_last_name,
        p.rp_first_name, p.rp_last_name,
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });

    return [...filtered].sort((a, b) => {
      const va = getVal(a, sortKey);
      const vb = getVal(b, sortKey);
      const cmp = typeof va === 'string' ? va.localeCompare(vb as string) : (va as number) - (vb as number);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [data, filter, typeFilter, mandantFilter, rmFilter, rpFilter, sortKey, sortDir]);

  const hasActiveFilter = Boolean(filter.trim() || typeFilter || mandantFilter || rmFilter || rpFilter);

  const clearFilters = () => {
    setFilter('');
    setTypeFilter('');
    setMandantFilter('');
    setRmFilter('');
    setRpFilter('');
  };

  // Export the currently visible rows (filters + sort applied) as .xlsx,
  // mirroring the table's columns.
  const exportExcel = () => {
    const header = [
      t('portfolio.col.participant'), t('portfolio.col.measureType'), t('portfolio.col.measures'),
      t('portfolio.col.totalMonths'), t('portfolio.col.daysRemaining'), t('portfolio.col.hoursPerWeek'),
      `${t('portfolio.col.activityRate')} (%)`, `${t('portfolio.col.targetRate')} (%)`, t('portfolio.export.presence30d'),
      t('portfolio.col.journalAge'), t('portfolio.col.mandant'), t('portfolio.col.rm'), t('portfolio.col.rp'),
    ];
    const rows = visible.map((p: any) => [
      `${p.last_name} ${p.first_name}`,
      typeDeMesure(p),
      p.measure_count ?? null,
      p.total_months ?? null,
      p.days_remaining ?? null,
      p.hours_per_week ?? null,
      p.taux_activite ?? null,
      p.taux_cible ?? null,
      p.presence_rate_30d != null ? Math.round(p.presence_rate_30d) : null,
      p.journal_days_stale ?? null,
      p.mandant_name || null,
      fullName(p.rm_last_name, p.rm_first_name) || null,
      fullName(p.rp_last_name, p.rp_first_name) || null,
    ]);
    const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);
    ws['!cols'] = [
      { wch: 24 }, { wch: 22 }, { wch: 9 }, { wch: 10 }, { wch: 13 },
      { wch: 15 }, { wch: 16 }, { wch: 14 }, { wch: 15 },
      { wch: 13 }, { wch: 20 }, { wch: 20 }, { wch: 20 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Portfolio');
    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `portfolio_${today}.xlsx`);
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s8)', marginBottom: 'var(--s12)' }}>
        <h1 style={{ fontSize: 'var(--t-17)', fontWeight: 700 }}>{t('portfolio.title')}</h1>
        <span className="stat-pill">{t('portfolio.participantCount', { n: visible.length })}{hasActiveFilter ? ` / ${data.length}` : ''}</span>
        <button
          className="btn btn-accent btn-sm"
          onClick={exportExcel}
          disabled={loading || visible.length === 0}
          style={{ marginLeft: 'auto' }}
          title={t('portfolio.exportExcelTooltip')}
        >
          {t('portfolio.exportExcel')}
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 'var(--s8)', marginBottom: 'var(--s12)' }}>
        <input
          type="search"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder={t('portfolio.searchPlaceholder')}
          style={{
            width: '100%', maxWidth: 280,
            padding: '6px 10px',
            fontSize: 'var(--t-12)',
            border: '1px solid var(--line)',
            borderRadius: 'var(--r2)',
            background: 'var(--surface)',
            color: 'var(--ink-1)',
          }}
        />
        <FilterSelect value={typeFilter} onChange={setTypeFilter} options={typeOptions} allLabel={t('portfolio.filter.typeAll')} />
        <FilterSelect value={mandantFilter} onChange={setMandantFilter} options={mandantOptions} allLabel={t('portfolio.filter.mandantAll')} />
        <FilterSelect value={rmFilter} onChange={setRmFilter} options={rmOptions} allLabel={t('portfolio.filter.rmAll')} />
        <FilterSelect value={rpFilter} onChange={setRpFilter} options={rpOptions} allLabel={t('portfolio.filter.rpAll')} />
        {hasActiveFilter && (
          <button className="btn btn-ghost btn-sm" onClick={clearFilters}>
            {t('portfolio.filter.reset')}
          </button>
        )}
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--s32)' }}><div className="spinner" /></div>
      ) : visible.length === 0 ? (
        <p style={{ color: 'var(--ink-3)', textAlign: 'center', padding: 'var(--s32)' }}>
          {hasActiveFilter ? t('portfolio.noResultsFilter') : t('portfolio.noActiveParticipants')}
        </p>
      ) : (
        <table className="table table-compact" style={{ tableLayout: 'auto' }}>
          <thead>
            <tr>
              <SortHeader sortKey="name" label={t('portfolio.col.participant')} currentKey={sortKey} currentDir={sortDir} onSort={onSort} />
              <SortHeader sortKey="programme" label={t('portfolio.col.measureType')} currentKey={sortKey} currentDir={sortDir} onSort={onSort} />
              <SortHeader sortKey="measures" label={t('portfolio.col.measures')} currentKey={sortKey} currentDir={sortDir} onSort={onSort} align="center" />
              <SortHeader sortKey="months" label={t('portfolio.col.totalMonths')} currentKey={sortKey} currentDir={sortDir} onSort={onSort} align="center" />
              <SortHeader sortKey="days" label={t('portfolio.col.daysRemaining')} currentKey={sortKey} currentDir={sortDir} onSort={onSort} align="center" />
              <SortHeader sortKey="hours" label={t('portfolio.col.hoursPerWeek')} currentKey={sortKey} currentDir={sortDir} onSort={onSort} align="center" />
              <SortHeader sortKey="taux_activite" label={t('portfolio.col.activityRate')} currentKey={sortKey} currentDir={sortDir} onSort={onSort} align="center" />
              <SortHeader sortKey="taux_cible" label={t('portfolio.col.targetRate')} currentKey={sortKey} currentDir={sortDir} onSort={onSort} align="center" title={t('portfolio.col.targetRateTooltip')} />
              <SortHeader sortKey="presence" label={t('portfolio.col.presence')} currentKey={sortKey} currentDir={sortDir} onSort={onSort} align="center" />
              <SortHeader sortKey="journal" label={t('portfolio.col.journalAge')} currentKey={sortKey} currentDir={sortDir} onSort={onSort} align="center" title={t('portfolio.col.journalAgeTooltip')} />
              <SortHeader sortKey="mandant" label={t('portfolio.col.mandant')} currentKey={sortKey} currentDir={sortDir} onSort={onSort} />
              <SortHeader sortKey="rm" label={t('portfolio.col.rm')} currentKey={sortKey} currentDir={sortDir} onSort={onSort} title={t('portfolio.col.rmTooltip')} />
              <SortHeader sortKey="rp" label={t('portfolio.col.rp')} currentKey={sortKey} currentDir={sortDir} onSort={onSort} title={t('portfolio.col.rpTooltip')} />
            </tr>
          </thead>
          <tbody>
            {visible.map((p: any, i: number) => (
              <tr
                key={`${p.measure_id}-${p.participant_id}`}
                className="table-row-clickable"
                style={{ cursor: 'pointer', backgroundColor: i % 2 === 1 ? 'var(--accent-dim)' : 'var(--surface)' }}
                onClick={() => { onOpenParticipant(p.participant_id, p.measure_id, firstFamilyName(p.last_name)); }}
              >
                <td style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                  <TruncText value={`${p.last_name} ${p.first_name}`} max={14} />
                </td>
                <td style={{ fontSize: 'var(--t-11)', whiteSpace: 'nowrap' }} title={p.subtype_name || p.programme_code}>
                  <TruncText value={p.subtype_name || p.programme_code || ''} max={18} />
                </td>
                {(() => {
                  const track = currentTrack(p.mandant_name);
                  const showRI = track === 'oai' && p.ri_measure_count > 0;
                  const showOAI = track === 'ri' && p.oai_measure_count > 0;
                  const altLabel = showRI ? t('portfolio.measuresRI') : showOAI ? t('portfolio.measuresOAI') : null;
                  const altCount = showRI ? p.ri_measure_count : showOAI ? p.oai_measure_count : null;
                  const altMonths = showRI ? p.ri_total_months : showOAI ? p.oai_total_months : null;
                  const noteStyle = { display: 'block', color: 'var(--ink-3)', fontSize: 'var(--t-10)', fontWeight: 400, marginTop: 1 };
                  return (
                    <>
                      <td style={{ textAlign: 'center' }}>
                        <span style={{ fontWeight: 600 }}>{p.measure_count ?? '—'}</span>
                        {altLabel && <span style={noteStyle}>{altLabel} : {altCount}</span>}
                      </td>
                      <td style={{ textAlign: 'center', color: 'var(--ink-2)' }}>
                        <span>{p.total_months != null ? p.total_months : '—'}</span>
                        {altLabel && altMonths != null && altMonths > 0 && (
                          <span style={noteStyle}>{altMonths}</span>
                        )}
                      </td>
                    </>
                  );
                })()}
                <td style={{ textAlign: 'center', fontWeight: 500, color: p.days_remaining != null ? levelColor(daysLevel(p.days_remaining)) : 'var(--ink-3)' }}>
                  {p.days_remaining != null ? p.days_remaining : '—'}
                </td>
                <td style={{ textAlign: 'center', color: 'var(--ink-1)' }}>
                  {p.hours_per_week != null ? fmtH(p.hours_per_week) : '—'}
                </td>
                <td style={{ textAlign: 'center', color: 'var(--ink-1)' }}>
                  {p.taux_activite != null ? `${p.taux_activite} %` : '—'}
                </td>
                <td style={{ textAlign: 'center', color: p.taux_cible != null ? 'var(--ink-2)' : 'var(--ink-3)' }} title={p.taux_cible != null && p.plan_progression_type === 'progressive' ? t('portfolio.planProgressive', { start: p.plan_start_pct, target: p.plan_target_pct, date: p.plan_target_date }) : p.taux_cible != null && p.plan_progression_type === 'constant' ? t('portfolio.planConstant', { pct: p.plan_target_pct }) : ''}>
                  {p.taux_cible != null ? `${p.taux_cible} %` : '—'}
                </td>
                <td style={{ textAlign: 'center', color: levelColor(presenceLevel(p.presence_rate_30d)) }}>
                  {p.presence_rate_30d != null ? `${Math.round(p.presence_rate_30d)} %` : '—'}
                </td>
                <td style={{ textAlign: 'center', color: p.journal_days_stale != null ? levelColor(journalLevel(p.journal_days_stale)) : 'var(--ink-3)' }}>
                  {p.journal_days_stale != null ? p.journal_days_stale : '—'}
                </td>
                <td style={{ color: 'var(--ink-3)', fontSize: 'var(--t-12)', whiteSpace: 'nowrap' }}>
                  {p.mandant_name ? <TruncText value={p.mandant_name} max={8} /> : '—'}
                </td>
                <td style={{ color: 'var(--ink-2)', fontSize: 'var(--t-12)', whiteSpace: 'nowrap' }}>
                  {firstFamilyName(p.rm_last_name) || '—'}
                </td>
                <td style={{ color: 'var(--ink-2)', fontSize: 'var(--t-12)', whiteSpace: 'nowrap' }}>
                  {firstFamilyName(p.rp_last_name) || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
